import {
  getArchiveHasComicInfo,
  getArchiveOpfRecord,
  type MetadataIdentifierScheme,
} from "@prose-reader/archive-reader"
import type { Archive } from "../archive/types"
import { COMIC_INFO_FILENAME, buildPatchedComicInfoXml } from "../comicInfo"
import { buildPatchedOpfXml } from "../opf/write"

/**
 * One identifier an archive should carry after the patch.
 *
 * `scheme` accepts any string: the containers hold arbitrary schemes and
 * the reader reports them verbatim. Known schemes are matched
 * case-insensitively, and `"Unknown"` means "carry no scheme".
 *
 * `unique` marks the identifier the OPF's `<package unique-identifier>`
 * points at. That element is structural — the writer only ever rewrites it
 * through this entry and never removes it, so a book keeps a resolvable
 * unique identifier whatever the caller does with the rest of the list.
 */
export type ArchiveMetadataIdentifier = {
  readonly scheme: MetadataIdentifierScheme
  readonly value: string
  readonly unique?: boolean
}

/**
 * Fields an archive patch may set. Expand in lockstep when a new field
 * becomes writable in at least one container.
 *
 * `identifiers` are written into the containers, creating or updating as
 * needed. `removedIdentifiers` are deleted, and **nothing else is** — an
 * identifier named in neither list is left exactly as the archive holds it, so
 * a patch that names no removals removes nothing.
 *
 * Naming removals rather than inferring them from absence is what keeps the
 * writer from deleting something no one saw: it has no way to tell an
 * identifier a caller chose to drop from one the caller never knew about,
 * because its reader reports fewer elements than the document contains.
 */
export type ArchiveMetadataPatch = {
  identifiers: ReadonlyArray<ArchiveMetadataIdentifier>
  removedIdentifiers?: ReadonlyArray<ArchiveMetadataIdentifier>
}

/**
 * Output containers the caller wants the patch written into. Set a
 * flag to `true` to opt that container in. The package never picks
 * targets on its own: deciding *where* metadata should live for a
 * given archive is product policy (and may diverge per reader app),
 * so it stays with the consumer.
 *
 * Consumers may call the writer once per container when each target
 * needs different values.
 */
export type ArchiveMetadataTargets = {
  comicInfo?: boolean
  opf?: boolean
}

/**
 * Describes one entry the caller should write back into the archive
 * to realize the patch. Each entry's `path` is archive-relative and
 * uses forward slashes; `xml` is a UTF-8 string ready to be stored.
 *
 * We emit a list — rather than a single path + body — so multi-target
 * patches (e.g. ComicInfo + OPF for hybrid archives) drop into the
 * same shape as single-target ones.
 */
export type ArchivePatchedEntry = {
  path: string
  xml: string
}

export type ArchivePatch = {
  entries: ArchivePatchedEntry[]
}

/**
 * Apply a metadata patch to an archive and return the XML bodies that
 * the caller should write back. The `targets` argument is required:
 * the package no longer picks where to write based on what's in the
 * archive — that decision belongs to the consumer.
 *
 * Per-target rules:
 *  - `targets.comicInfo` → patch the existing `ComicInfo.xml` if any,
 *    otherwise synthesize a minimal one. This always succeeds and is
 *    the safe default for archives that carry no embedded metadata.
 *    Identifiers are mapped onto `<GTIN>` and `<Web>`, so ones on a
 *    scheme neither field represents are not written for this target
 *    — see `isComicInfoWritableIdentifierScheme` to check up front.
 *  - `targets.opf` → patch the existing OPF package document. Throws
 *    when the archive has no OPF: synthesizing one would turn a CBZ
 *    into an EPUB, which is well outside this writer's scope.
 *
 * Calling with no targets selected is a programming error — the
 * caller's policy should always pick at least one container.
 *
 * The caller keeps ownership of how the archive is repacked — each
 * runtime plugs in its own write-capable zip library (JSZip on the
 * web; something else on the server when that lands). Containers are
 * located with the same prose-reader lookups the read side uses, so a
 * container the reader sees is the container the writer patches.
 */
export const patchArchiveMetadata = async (
  archive: Archive,
  patch: ArchiveMetadataPatch,
  targets: ArchiveMetadataTargets,
): Promise<ArchivePatch> => {
  if (!targets.comicInfo && !targets.opf) {
    throw new Error(
      "patchArchiveMetadata requires at least one target (comicInfo or opf).",
    )
  }

  const entries: ArchivePatchedEntry[] = []

  if (targets.comicInfo) {
    const xml = await buildPatchedComicInfoXml(archive, patch)
    const existingComicInfo = getArchiveHasComicInfo(archive)
    entries.push({ path: existingComicInfo?.uri ?? COMIC_INFO_FILENAME, xml })
  }

  if (targets.opf) {
    const opfEntry = await getArchiveOpfRecord(archive)

    if (!opfEntry) {
      throw new Error(
        "Cannot write OPF metadata: archive does not carry an OPF package document.",
      )
    }

    const xml = await buildPatchedOpfXml(opfEntry, patch)
    entries.push({ path: opfEntry.uri, xml })
  }

  return { entries }
}
